/**
 * Date utility functions for TrackVibes
 * Centralized date formatting with i18n support
 */

// Locale mapping for date formatting
const LOCALE_MAP = {
  fr: 'fr-FR',
  en: 'en-US',
  es: 'es-ES',
  de: 'de-DE',
  pt: 'pt-PT'
};

/**
 * Get the locale string for date formatting
 * @param {string} lang - Language code (fr, en, es, de, pt)
 * @returns {string} - Locale string (fr-FR, en-US, etc.)
 */
export const getLocale = (lang = 'fr') => LOCALE_MAP[lang] || 'fr-FR';

/**
 * Format a date string to a localized date and time
 * @param {string|Date} dateStr - Date string or Date object
 * @param {string} lang - Language code (fr, en, es, de, pt)
 * @returns {string} - Formatted date and time string
 */
export const formatDateTime = (dateStr, lang = 'fr') => {
  if (!dateStr) return '-';
  try {
    const locale = getLocale(lang);
    return new Date(dateStr).toLocaleString(locale, {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    console.warn('[formatDateTime] Error formatting date:', e);
    return String(dateStr);
  }
};

/**
 * Format a date string to a localized date only
 * @param {string|Date} dateStr - Date string or Date object
 * @param {string} lang - Language code (fr, en, es, de, pt)
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateStr, lang = 'fr') => {
  if (!dateStr) return '-';
  try {
    const locale = getLocale(lang);
    return new Date(dateStr).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  } catch (e) {
    console.warn('[formatDate] Error formatting date:', e);
    return String(dateStr);
  }
};

/**
 * Format a date string to a short date
 * @param {string|Date} dateStr - Date string or Date object
 * @param {string} lang - Language code (fr, en, es, de, pt)
 * @returns {string} - Formatted short date string
 */
export const formatDateShort = (dateStr, lang = 'fr') => {
  if (!dateStr) return '-';
  try {
    const locale = getLocale(lang);
    return new Date(dateStr).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch (e) {
    console.warn('[formatDateShort] Error formatting date:', e);
    return String(dateStr);
  }
};

/**
 * Format a time string
 * @param {string|Date} dateStr - Date string or Date object
 * @param {string} lang - Language code (fr, en, es, de, pt)
 * @returns {string} - Formatted time string
 */
export const formatTime = (dateStr, lang = 'fr') => {
  if (!dateStr) return '-';
  try {
    const locale = getLocale(lang);
    return new Date(dateStr).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (e) {
    console.warn('[formatTime] Error formatting time:', e);
    return String(dateStr);
  }
};

/**
 * Format duration in minutes to human-readable string
 * @param {number} minutes - Duration in minutes
 * @returns {string} - Formatted duration string
 */
export const formatDuration = (minutes) => {
  if (!minutes) return '-';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}min`;
  return `${hours}h${mins > 0 ? mins : ''}`;
};

/**
 * Get relative time string (e.g., "il y a 5 minutes")
 * @param {string|Date} dateStr - Date string or Date object
 * @param {string} lang - Language code (fr, en, es, de, pt)
 * @returns {string} - Relative time string
 */
export const formatRelativeTime = (dateStr, lang = 'fr') => {
  if (!dateStr) return '-';
  try {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    const labels = {
      fr: { now: 'maintenant', min: 'min', hour: 'h', day: 'j', ago: 'il y a' },
      en: { now: 'just now', min: 'm', hour: 'h', day: 'd', ago: '' },
      es: { now: 'ahora', min: 'min', hour: 'h', day: 'd', ago: 'hace' },
      de: { now: 'jetzt', min: 'min', hour: 'h', day: 'T', ago: 'vor' },
      pt: { now: 'agora', min: 'min', hour: 'h', day: 'd', ago: 'há' }
    };
    const l = labels[lang] || labels.fr;

    if (diffMins < 1) return l.now;
    if (diffMins < 60) return `${l.ago} ${diffMins}${l.min}`.trim();
    if (diffHours < 24) return `${l.ago} ${diffHours}${l.hour}`.trim();
    return `${l.ago} ${diffDays}${l.day}`.trim();
  } catch (e) {
    return String(dateStr);
  }
};
